import type { OnDestroy } from '@angular/core'
import { Injectable, effect, inject, signal } from '@angular/core'
import { apiHostname, app, buildName, environment, releaseName, version } from '@awork/environments/environment'
import { breadcrumbsIntegration, captureException, init, setUser, setExtra, withScope } from '@sentry/angular'
import type { ErrorEvent, EventHint } from '@sentry/angular'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { UserQuery } from '@awork/features/user/state/user.query'
import { AppQuery } from '@awork/core/state/app.query'
import { AppStore } from '@awork/core/state/app.store'
import type { HttpErrorResponse } from '@angular/common/http'
import { LogsStore } from '@awork/framework/state/logs.store'
import { AccountQuery } from '@awork/_shared/state/account.query'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import isTest from '@awork/_shared/functions/is-test'
import { classifyError, type ErrorType } from '@awork/_shared/functions/classify-error'
import type { ErrorItem, LoggingExtraInfo } from '@awork/_shared/services/log-service/types'
import { Level } from '@awork/_shared/services/log-service/types'
import { createDatadogRumCostConfiguration } from '@awork/_shared/services/log-service/datadog-rum-config'
import { removeDatadogCrossSubdomainSessionCookie } from '@awork/_shared/services/log-service/datadog-session-cookie'
import { prepareBrowserErrorEvent } from '@awork/_shared/services/log-service/sentry-browser-errors'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import type { User } from '@awork/features/user/models/user.model'
import type { Workspace } from '@awork/features/workspace/models/workspace.model'
import type { AccountState } from '@awork/_shared/models/account.model'

interface AppContext {
  user?: User
  workspace?: Workspace
  account?: AccountState
  traces?: { traceIds: string[]; traceIdsError: string[] }
  codeVersion?: string
  appVersion: string
}

interface ErrorContext extends AppContext {
  errorType?: ErrorType
  extraInfo: LoggingExtraInfo
}

interface LoggingContext extends AppContext {
  stackTrace: string
  extraInfo: LoggingExtraInfo
}

@Injectable({
  providedIn: 'root'
})
export class LogService implements OnDestroy {
  private workspaceQuery = inject(WorkspaceQuery)
  private accountQuery = inject(AccountQuery)
  private appQuery = inject(AppQuery)
  private userQuery = inject(UserQuery)
  private appStore = inject(AppStore)
  private logsStore = inject(LogsStore)
  private featureFlagQuery = inject(FeatureFlagQuery)
  private readonly sentryReady = signal(false)
  private readonly currentSentryUser = this.userQuery.queryCurrentUser()
  private readonly currentSentryWorkspace = this.workspaceQuery.queryCurrentWorkspace()

  errors: ErrorItem[] = []
  httpErrors: ErrorItem[] = []
  debug: ErrorItem[] = []
  appVersion: string
  static instance: LogService
  protected readonly datadogApplicationId = '1fc312b7-6333-4385-8d08-5995328c67f2'
  protected readonly datadogClientToken = 'pub4a73c100e478297279fb8de37538eafc'

  protected readonly isDatadogLogsEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.datadogLogging)

  constructor() {
    LogService.instance = this

    const account = this.accountQuery.queryAccountState()

    // Capacitor copies this scope to the native SDK, including logout and workspace changes.
    effect(() => {
      if (!this.sentryReady()) {
        return
      }

      const currentUser = this.currentSentryUser()
      const currentWorkspace = this.currentSentryWorkspace()

      setUser(currentUser ? { id: currentUser.id, email: account().email, username: currentUser.fullName } : null)
      setExtra('workspace', currentWorkspace ? { id: currentWorkspace.id, name: currentWorkspace.name } : null)
    })
  }

  /** Starts scope updates after the SDK is ready, so native reports receive the current session too. */
  enableSentryContext(): void {
    this.sentryReady.set(true)
  }

  /**
   * Adds current app context to JavaScript events from either client.
   * @param {ErrorEvent} event Captured event.
   * @param {EventHint} hint Original error used for browser source details and classification.
   * @returns {ErrorEvent | null} Enriched event, or null for a browser notification that must be ignored.
   */
  prepareSentryEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
    const prepared = prepareBrowserErrorEvent(event, hint.originalException)

    if (!prepared) {
      return null
    }

    const user = this.currentSentryUser()
    const account = this.accountQuery.getAccount()
    const workspace = this.currentSentryWorkspace()
    const traces = this.appQuery.getTraces()

    prepared.user = user ? { ...prepared.user, id: user.id, email: account?.email, username: user.fullName } : undefined
    prepared.tags = { buildName, releaseName, ...prepared.tags }
    prepared.extra = {
      ...prepared.extra,
      traceIds: traces.traceIds,
      traceIdsError: traces.traceIdsError
    }

    if (workspace) {
      prepared.extra.workspace = { id: workspace.id, name: workspace.name }
    } else {
      delete prepared.extra.workspace
    }

    if (this.appVersion) {
      prepared.extra.appVersion = this.appVersion
    }

    if (!prepared.tags.errorType && hint.originalException instanceof Error) {
      prepared.tags.errorType = classifyError(hint.originalException)
    }

    prepared.request = {
      ...prepared.request,
      url: window.location.href,
      headers: { ...prepared.request?.headers, 'User-Agent': navigator.userAgent }
    }
    prepared.platform = 'javascript'

    return prepared
  }

  /**
   * Clears the static reference when Angular destroys this service.
   */
  ngOnDestroy(): void {
    if (LogService.instance === this) {
      Reflect.deleteProperty(LogService, 'instance')
    }
  }

  /**
   * Stores debug information
   * @param {Error} error
   */
  logDebug(error: Error): void {
    if (environment !== 'production') {
      this.logsStore.update(state => {
        let debug = [...state.debug]

        debug.unshift({ date: new Date(), error })
        debug = debug.slice(0, 100)

        return { debug }
      })
    }
  }

  /**
   * Stores the trace id of last request
   * @param {string} traceId
   * @param {boolean} error
   */
  logTrace(traceId: string, error: boolean): void {
    if (traceId && this.appQuery.getDebug()) {
      this.appStore.logTrace(traceId, error)
    }
  }

  /**
   * Logs the error in Sentry external service and optionally in Datadog
   * @param {Error} error
   * @param {string} codeVersion
   * @param {string} appVersion
   * @param {ErrorType} errorType
   * @param {LoggingExtraInfo} extraInfo
   * @returns {string}
   */
  logError(
    error: Error,
    codeVersion?: string,
    appVersion?: string,
    errorType?: ErrorType,
    extraInfo?: LoggingExtraInfo
  ): string {
    if (environment === 'local') {
      // return undefined
    }

    if (!(error instanceof Error)) {
      const internalErrorMessage = JSON.stringify(error)

      error = new Error(internalErrorMessage)
      error.name = 'INTERNAL_ERROR'
    }

    const user = this.userQuery.getCurrentUser()
    const workspace = this.workspaceQuery.getCurrentWorkspace()
    const account = this.accountQuery.getAccount()
    const traces = this.appQuery.getTraces()
    const isDatadogLogsEnabled = this.isDatadogLogsEnabled()

    // Log to Sentry (existing implementation)
    const eventId = this.logErrorToSentry(error, {
      user,
      workspace,
      account,
      traces,
      codeVersion,
      appVersion: appVersion || this.appVersion,
      errorType,
      extraInfo
    })

    // Log to Datadog if feature flag is enabled
    if (isDatadogLogsEnabled) {
      this.logErrorToDatadog(error, {
        user,
        workspace,
        account,
        traces,
        appVersion: this.appVersion,
        codeVersion,
        errorType,
        extraInfo
      })
    }

    return eventId
  }

  /**
   * Logs error to Sentry
   * @param {Error} error
   * @param {ErrorContext} context
   * @returns {string}
   */
  logErrorToSentry(error: Error, context: ErrorContext): string {
    let eventId: string
    const { user, workspace, account, traces, errorType, extraInfo } = context

    withScope(scope => {
      scope.setUser({
        id: user?.id,
        email: account?.email,
        username: user?.fullName
      })

      scope.setTag('buildName', buildName)
      scope.setTag('releaseName', releaseName)

      scope.setExtra('traceIds', traces.traceIds)
      scope.setExtra('traceIdsError', traces.traceIdsError)

      if (workspace) {
        scope.setExtra('workspace', { id: workspace.id, name: workspace.name })
      }

      if (this.appVersion) {
        scope.setExtra('appVersion', this.appVersion)
      }

      if (errorType) {
        scope.setTag('errorType', errorType)
      }

      if (extraInfo) {
        scope.setExtras(extraInfo)
      }

      eventId = captureException(error)
    })

    return eventId
  }

  /**
   * Logs a message to Datadog browser logs.
   * @param {Level} level
   * @param {string} message
   * @param {LoggingExtraInfo} extraInfo
   */
  logMessage(level: Level, message: string, extraInfo?: LoggingExtraInfo): void {
    if (environment === 'local') {
      // return undefined
    }

    const user = this.userQuery.getCurrentUser()
    const workspace = this.workspaceQuery.getCurrentWorkspace()
    const account = this.accountQuery.getAccount()
    const stackTrace = this.getStackTrace()

    this.logMessageToDatadog(level, message, {
      user,
      workspace,
      account,
      appVersion: this.appVersion,
      stackTrace,
      extraInfo
    })
  }

  /**
   * Logs error to Datadog browser logs
   * @param {Error} error
   * @param {ErrorContext} context
   * @private
   */
  private logErrorToDatadog(error: Error, context: ErrorContext): void {
    try {
      const correlationId = this.generateCorrelationId()
      const redactedMessage = this.redactEmails(error.message)

      datadogLogs.logger.error(redactedMessage, {
        error: {
          name: error.name,
          message: redactedMessage,
          stack: this.redactEmails(error.stack)
        },
        // Only pseudonymous IDs. Do not send email, names, or the workspace name to browser logs.
        user: context.user
          ? {
              id: context.user.id
            }
          : undefined,
        workspace: context.workspace
          ? {
              id: context.workspace.id
            }
          : undefined,
        build: {
          buildName,
          releaseName,
          appVersion: context.appVersion,
          codeVersion: context.codeVersion
        },
        traces: {
          traceIds: context.traces?.traceIds,
          traceIdsError: context.traces?.traceIdsError
        },
        errorType: context.errorType,
        correlationId,
        ...this.redactEmailsDeep(context.extraInfo)
      })
    } catch (datadogError) {
      this.handleDatadogError(datadogError, context)
    }
  }

  /**
   * Logs message to Datadog browser logs
   * @param {Level} level
   * @param {string} message
   * @param {LoggingContext} context
   */
  logMessageToDatadog(level: Level, message: string, context: LoggingContext): void {
    try {
      const correlationId = this.generateCorrelationId()

      message = this.redactEmails(message)

      const metadata = {
        ...this.redactEmailsDeep(context.extraInfo),
        // Only pseudonymous IDs. Do not send email, names, or the workspace name to browser logs.
        user: context.user
          ? {
              id: context.user.id
            }
          : undefined,
        workspace: context.workspace
          ? {
              id: context.workspace.id
            }
          : undefined,
        buildName,
        releaseName,
        appVersion: context.appVersion,
        build: {
          buildName,
          releaseName,
          appVersion: context.appVersion
        },
        stackTrace: context.stackTrace,
        errorType: 'message',
        correlationId
      }

      switch (level) {
        case Level.debug:
          datadogLogs.logger.debug(message, metadata)
          return
        case Level.info:
          datadogLogs.logger.info(message, metadata)
          return
        case Level.warn:
          datadogLogs.logger.warn(message, metadata)
          return
        case Level.error:
        case Level.fatal:
          datadogLogs.logger.error(message, metadata)
          return
        default:
          datadogLogs.logger.info(message, metadata)
          return
      }
    } catch (datadogError) {
      this.handleDatadogMessageError(datadogError)
    }
  }

  /**
   * Stores Datadog message logging failures locally without falling back to Sentry.
   * @param {unknown} datadogError
   * @private
   */
  private handleDatadogMessageError(datadogError: unknown): void {
    const newError = new Error('Failed to log message to Datadog')

    newError.name = 'DATADOG_LOG_ERROR'

    if (datadogError instanceof Error) {
      newError.message = datadogError.message
      newError.stack = datadogError.stack
    }

    this.saveError(newError)
  }

  /**
   * Handles errors that occur during logging to Datadog
   * @param {unknown} datadogError
   * @param {LoggingContext | ErrorContext} context
   * @private
   */
  private handleDatadogError(datadogError: unknown, context: LoggingContext | ErrorContext): void {
    const newError = new Error()

    newError.name = 'DATADOG_LOG_ERROR'
    newError.message = 'Failed to log message to Datadog'

    if (datadogError instanceof Error) {
      newError.message = datadogError?.message
      newError.stack = datadogError.stack
    } else {
      context.extraInfo = { ...context.extraInfo, datadogError }
    }

    this.logErrorToSentry(newError, context)
  }

  /**
   * Replaces email addresses in log text. Callers must not put direct identity into
   * log messages, but this keeps a single mistake from reaching Datadog.
   * @param {string} text
   * @returns {string}
   * @private
   */
  private redactEmails(text: string): string {
    return text?.replace(/[^\s@"'`<>()[\]{}]+@[^\s@"'`<>()[\]{}]+\.[a-z]{2,}/gi, '<redacted-email>')
  }

  /**
   * Redacts email addresses from every string value in the extra info before it is sent
   * to Datadog. Returns undefined when the value cannot be serialized.
   * @param {LoggingExtraInfo} extraInfo
   * @returns {LoggingExtraInfo}
   * @private
   */
  private redactEmailsDeep(extraInfo?: LoggingExtraInfo): LoggingExtraInfo {
    if (!extraInfo) {
      return undefined
    }

    try {
      return JSON.parse(
        JSON.stringify(extraInfo, (_, value: unknown) => (typeof value === 'string' ? this.redactEmails(value) : value))
      )
    } catch {
      return undefined
    }
  }

  /**
   * Generates a client-side correlation ID for tracking
   * @returns {string}
   */
  generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Workaround to get a stacktrace for log messages.
   * It creates a stack trace by creating a fake error
   * @returns {string}
   * @private
   */
  private getStackTrace(): string {
    const stackTrace = new Error()?.stack

    // Remove the first 3 lines of the stack trace:
    // One is from the Error constructor,
    // the other is from this function
    // and the third is from the logMessage function
    return stackTrace?.split?.('\n')?.slice?.(3).join?.('\n') || stackTrace
  }

  /**
   * Initializes Sentry SDK
   * @param {string} codeVersion
   * @param {string} appVersion
   */
  initSentry(codeVersion?: string, appVersion?: string): void {
    // Web init
    if (app === 'web' && !isTest()) {
      init({
        dsn: 'https://83a1c507839a4bc2a510d5b9262e1580@o394300.ingest.sentry.io/5556977',
        release: !codeVersion ? version : codeVersion,
        environment,
        defaultIntegrations: false,
        integrations: [
          breadcrumbsIntegration({
            console: false,
            dom: true,
            fetch: false,
            history: true,
            sentry: false,
            xhr: false
          })
        ],
        beforeSend: (event, hint) => this.prepareSentryEvent(event, hint)
      })
      this.enableSentryContext()
    }

    this.appVersion = appVersion
  }

  /**
   * Save javascript errors to display them in console modal
   * @param {Error} error
   */
  saveError(error: Error): void {
    if (environment !== 'production' && error.message) {
      this.logsStore.update(state => {
        let errors = [...state.errors]

        errors.unshift({ date: new Date(), error })
        errors = errors.slice(0, 20)

        return { errors }
      })
    }
  }

  /**
   * Save http errors to display them in console modal
   * @param {HttpErrorResponse} error
   */
  saveHttpError(error: HttpErrorResponse): void {
    if (environment !== 'production') {
      const traceId = error.headers ? error.headers.get('trace-id') : null

      this.logsStore.update(state => {
        let httpErrors = [...state.httpErrors]

        httpErrors.unshift({ date: new Date(), error, traceId })
        httpErrors = httpErrors.slice(0, 20)

        return { httpErrors }
      })
    }
  }

  /**
   * Initializes Datadog RUM
   */
  initDatadogRum(): void {
    if (app !== 'web' || environment !== 'production' || isTest()) {
      return
    }

    const datadogRumSampleRate = this.featureFlagQuery.queryFlag(FeatureFlag.datadogRum)

    if (!datadogRumSampleRate()) {
      return
    }

    const datadogReplaySampleRate = this.featureFlagQuery.queryFlag(FeatureFlag.datadogReplay)
    const allowedTrackingUrlsRegex = new RegExp(`https://.*\\.${apiHostname.replace('.', '\\.')}/`)

    removeDatadogCrossSubdomainSessionCookie()

    // Initialize Datadog RUM
    datadogRum.init({
      applicationId: this.datadogApplicationId,
      clientToken: this.datadogClientToken,
      site: 'datadoghq.eu',
      service: 'web-app',
      env: environment,
      version: version,
      ...createDatadogRumCostConfiguration(datadogRumSampleRate(), datadogReplaySampleRate()),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [allowedTrackingUrlsRegex],
      beforeSend: (event, context) => {
        // Collect and send trace id from xhr requests
        if (
          event.type === 'resource' &&
          event.resource.type === 'xhr' &&
          event.resource.url.includes(apiHostname) &&
          context?.['xhr']
        ) {
          event.context.responseHeaders = {
            'trace-id': context['xhr'].getResponseHeader('trace-id')
          }
        }

        return true
      }
    })
  }

  /**
   * Initializes Browser logs
   */
  initDatadogLogs(): void {
    if (app !== 'web' || environment === 'local' || isTest()) {
      return
    }

    removeDatadogCrossSubdomainSessionCookie()

    datadogLogs.init({
      clientToken: this.datadogClientToken,
      site: 'datadoghq.eu',
      service: 'web-app-logs',
      env: environment,
      version: version,
      forwardErrorsToLogs: false,
      sessionSampleRate: 100
    })
  }
}
